import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import ArticleSchemaMarkup from "./ArticleSchemaMarkup.jsx";

export default function VocalsSitInMixLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const quickFacts = [
    {
      title: "What it means",
      text: "A vocal sits in the mix when it stays clear, steady, and connected to the track without feeling pasted on top.",
      icon: <MicRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "What usually goes wrong",
      text: "Uneven level, harshness, muddy low mids, masking from instruments, or too much reverb can all make the vocal feel out of place.",
      icon: <HeadphonesRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Best first move",
      text: "Get the vocal level consistent before chasing tone. A steady vocal is much easier to EQ, compress, and place.",
      icon: <VolumeUpRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const commonProblems = [
    {
      title: "The vocal disappears in parts",
      description:
        "This usually means the level is not consistent enough, or other instruments are masking the vocal during busier sections.",
    },
    {
      title: "The vocal sounds too loud but still unclear",
      description:
        "This often points to tone, not volume. The vocal may need presence, less low-mid buildup, better compression, or less masking from the arrangement.",
    },
    {
      title: "The vocal feels disconnected",
      description:
        "A vocal can feel detached when it is too dry, too bright, too compressed, or not sharing the same space as the rest of the mix.",
    },
    {
      title: "The vocal is harsh or tiring",
      description:
        "Harshness usually comes from too much upper-mid energy, sibilance, or compression that pushes sharp details forward.",
    },
  ];

  const steps = [
    {
      title: "Start with the raw vocal level",
      description:
        "Before you add a full processing chain, listen to the vocal against the instrumental. If some words jump out and others disappear, the vocal will be hard to place no matter what EQ or reverb you use. Start by getting the performance into a more even range.",
    },
    {
      title: "Control the big level moves",
      description:
        "Use clip gain, automation, an auto leveler, or careful compression to reduce the biggest level differences. This is not about making the vocal flat. It is about keeping the listener from constantly reaching for the volume knob.",
    },
    {
      title: "Clean up unnecessary low end",
      description:
        "Most vocals do not need much deep low-end energy. A high-pass filter can remove rumble, breath noise, mic stand vibration, and proximity buildup. The key is to stop before the vocal loses body.",
    },
    {
      title: "Manage the low mids",
      description:
        "The low mids give vocals weight, but too much can make them cloudy or boxy. Listen around the lower body of the vocal and make small cuts only where the tone is masking clarity.",
    },
    {
      title: "Shape presence without making it harsh",
      description:
        "A vocal often needs upper-mid presence to cut through the mix. The trick is adding enough focus for the words to read clearly without turning the vocal sharp or fatiguing.",
    },
    {
      title: "Use compression for control and density",
      description:
        "Compression helps the vocal stay forward and consistent. A slower attack can preserve some natural articulation, while a faster attack can clamp down on peaks. The right choice depends on the performance and style.",
    },
    {
      title: "Control sibilance",
      description:
        "If S, T, and SH sounds are jumping out, the vocal may feel harsh even if the overall tone is good. A de-esser can control those sharp moments without darkening the entire track.",
    },
    {
      title: "Create space with reverb and delay",
      description:
        "Reverb and delay help the vocal belong in the track, but too much pushes it back. Use space to connect the vocal to the production, not to hide it.",
    },
    {
      title: "Use automation as the finishing move",
      description:
        "Even after leveling and compression, a vocal often needs small rides to feel finished. Automation helps important words stay present and keeps emotional moments from getting lost.",
    },
  ];

  const frequencyAreas = [
    {
      range: "80–150 Hz",
      title: "Low-end buildup",
      description:
        "Can add weight, but too much creates rumble and proximity boom that gets in the way of kick and bass.",
    },
    {
      range: "200–500 Hz",
      title: "Mud and boxiness",
      description:
        "This area can make vocals sound thick, cloudy, or closed-in if it builds up too much.",
    },
    {
      range: "1–3 kHz",
      title: "Intelligibility",
      description:
        "Important for lyric clarity and presence. Too little can bury the vocal; too much can feel nasal or pushy.",
    },
    {
      range: "4–8 kHz",
      title: "Edge and sibilance",
      description:
        "Adds detail and brightness, but can become sharp, spitty, or tiring if uncontrolled.",
    },
    {
      range: "10 kHz+",
      title: "Air",
      description:
        "Can open the vocal and add lift, but it works best after harshness and sibilance are already controlled.",
    },
  ];

  const bestPractices = [
    "Balance the vocal against the full mix, not just in solo.",
    "Fix level consistency before making aggressive EQ decisions.",
    "Use EQ cuts to make room before relying on boosts to force the vocal forward.",
    "Control sibilance before adding a lot of air or brightness.",
    "Use reverb and delay sends instead of drowning the vocal directly in effects.",
    "Automate important words and phrase endings so the performance stays connected.",
    "Check the vocal at low volume. If the words disappear, the vocal is not sitting yet.",
  ];

  const relatedLinks = [
    { label: "Compression", to: "/learning/compression" },
    { label: "De-Esser", to: "/learning/de-esser" },
    { label: "Equalization", to: "/learning/equalization" },
    { label: "Reverb", to: "/learning/reverb" },
    { label: "Automation in Mixing", to: "/learning/automation-in-mixing" },
    { label: "How to Fix a Muddy Mix", to: "/learning/fix-muddy-mix" },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="How to Make Vocals Sit in the Mix | Parish Audio Learning"
        description="Learn how to make vocals sit in the mix with better leveling, compression, EQ, de-essing, reverb, delay, and automation."
        canonical="https://parishaudio.com/learning/make-vocals-sit-in-mix"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for a Parish Audio article about making vocals sit in a mix"
      />

      <ArticleSchemaMarkup
        canonical="https://parishaudio.com/learning/make-vocals-sit-in-mix"
        headline="How to Make Vocals Sit in the Mix"
        description="Learn how to make vocals sit in the mix with better leveling, compression, EQ, de-essing, reverb, delay, and automation."
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        articleSection="Audio Education"
        keywords={[
          "how to make vocals sit in the mix",
          "vocal mixing",
          "mix vocals",
          "vocal processing",
          "vocal compression",
          "vocal EQ",
          "vocal leveling",
          "music production",
        ]}
        breadcrumbs={[
          { name: "Home", item: "https://parishaudio.com/" },
          { name: "Learning", item: "https://parishaudio.com/learning" },
          {
            name: "How to Make Vocals Sit in the Mix",
            item: "https://parishaudio.com/learning/make-vocals-sit-in-mix",
          },
        ]}
        datePublished="2026-05-02T00:00:00-06:00"
        dateModified="2026-05-02T00:00:00-06:00"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 280, md: 340 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.86) 0%, rgba(8,12,18,0.75) 38%, rgba(8,12,18,0.46) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 820 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Vocal Mixing Guide"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.15rem", md: "3.4rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      How to Make Vocals Sit in the Mix
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 740,
                      }}
                    >
                      A vocal that sits well does not just sound louder. It feels connected
                      to the track, stays clear through the whole performance, and holds
                      the listener’s attention without covering the music around it. The
                      goal is balance, not brute force.
                    </Typography>

                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      Published May 2, 2026
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.55 }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Why Vocals Often Feel Separate From the Mix
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Vocals are usually the most important part of a song, podcast, or
                    spoken production, so it is easy to turn them up when they are not
                    working. Sometimes that helps, but often it makes the problem more
                    obvious. The vocal gets louder, but it still does not feel settled.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    That happens because “sitting in the mix” is not just a volume issue.
                    It is a combination of level consistency, frequency balance, dynamics,
                    space, and arrangement. If the vocal is jumping around in level, fighting
                    guitars or keys, or sitting in a different space than the track, it will
                    feel disconnected even if the fader is in the right place.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common Vocal Placement Problems
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {commonProblems.map((problem) => (
                      <Paper key={problem.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.5 }}>
                          {problem.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {problem.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Step-by-Step: Getting the Vocal Into Place
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {steps.map((step, index) => (
                        <Paper key={step.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.6}>
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {index + 1}. {step.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {step.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Useful Vocal Frequency Areas
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      These ranges are starting points, not rules. Always make the final
                      decision while listening to the full mix.
                    </Typography>

                    <Stack spacing={1.2}>
                      {frequencyAreas.map((area) => (
                        <Paper key={area.range} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.7}>
                            <Chip
                              label={area.range}
                              size="small"
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: "5px",
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontWeight: 700,
                              }}
                            />
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {area.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {area.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompressRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Leveling and Compression Work Better Together
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A vocal that changes level too much is difficult to mix. Compression
                    helps, but it is not always the cleanest way to handle every level
                    problem by itself. If the compressor is forced to catch every big jump,
                    it can start to sound obvious, pinned down, or uneven.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A better approach is often to use leveling and compression together.
                    Leveling keeps the overall vocal more consistent, while compression
                    catches faster peaks and shapes the feel. That way, neither processor
                    has to do all the work.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    This is especially useful for spoken vocals, podcasts, voiceovers, and
                    dynamic singers. When the level stays steady, EQ decisions become easier,
                    reverbs behave more predictably, and the vocal stays easier to follow.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: accent }} />
                          <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                            Quick listening test
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                          Turn the mix down low. If the vocal still feels understandable
                          and connected to the track, you are close. If the music stays
                          clear but the words disappear, the vocal needs better level,
                          presence, or space control.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Tools That Help
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      A focused vocal chain usually needs level control, compression, tone
                      shaping, sibilance control, and space. The exact tools depend on the
                      source, but the goal stays the same: keep the vocal clear, steady,
                      and connected to the track.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Auto Level can help keep the overall vocal level steady, especially
                      before compression. Vocal Pro is built for shaping and finishing vocal
                      tracks with the core tools you need in one place.
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        component={RouterLink}
                        to="/products/auto-level"
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: style.accentGreen,
                          "&:hover": {
                            backgroundColor: style.accentGreen,
                            filter: "brightness(1.05)",
                          },
                        }}
                      >
                        View Auto Level
                      </Button>

                      <Button
                        component={RouterLink}
                        to="/products/vocal-pro"
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: `1px solid ${accent}`,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        View Vocal Pro
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                    >
                      Related Topics
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    These articles connect directly to vocal placement, vocal tone, and mix clarity.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {relatedLinks.map((link) => (
                      <Button
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: `1px solid ${accent}`,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}